import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Clock, BookOpen, Award, Users, CheckCircle, Play, Search, Filter, GraduationCap } from 'lucide-react';
import axios from 'axios';
import SEO from '@/components/SEO';
import SectionLabel from '@/components/SectionLabel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const YouthPage = () => {
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(12);

  useEffect(() => {
    // Load user's enrollments from localStorage
    const saved = localStorage.getItem('sbt_enrollments');
    if (saved) {
      setEnrollments(JSON.parse(saved));
    }
    
    // Fetch courses from API
    const fetchCourses = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/courses`);
        if (response.data && response.data.courses) {
          setCourses(response.data.courses);
        }
      } catch (error) {
        console.error('Error fetching courses:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCourses();
  }, []);

  // Get unique categories from courses
  const categories = ['All', ...new Set(courses.map(c => c.category).filter(Boolean))];

  // Filter courses by category and search
  const filteredCourses = courses.filter(course => {
    const matchesCategory = selectedCategory === 'All' || course.category === selectedCategory;
    const matchesSearch = !searchQuery || 
      course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const visibleCourses = filteredCourses.slice(0, visibleCount);

  const isEnrolled = (courseId) => enrollments.some(e => e.courseId === courseId);

  const handleEnroll = (courseId) => {
    navigate(`/youth/course/${courseId}`);
  };

  const loadMore = () => {
    setVisibleCount(prev => prev + 12);
  };

  // Calculate stats
  const totalModules = courses.reduce((acc, c) => acc + (c.module_count || c.modules?.length || 3), 0);

  return (
    <div data-testid="youth-page" className="min-h-screen">
      <SEO 
        title="Youth Programme — Free Leadership Courses for Women"
        description="Free self-paced courses on leadership, entrepreneurship, and business for young women. Learn from verified women leaders, complete quizzes, and earn professional certificates."
        keywords={['women leadership courses', 'free leadership training', 'women entrepreneurship program', 'female leadership development', 'youth leadership courses', 'women in business education']}
        url="https://shebuiltthis.com/youth"
        image="https://images.pexels.com/photos/7713364/pexels-photo-7713364.jpeg?auto=compress&cs=tinysrgb&w=1200"
      />
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center film-grain overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.pexels.com/photos/7713364/pexels-photo-7713364.jpeg?auto=compress&cs=tinysrgb&w=1920')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#080808] via-[#080808]/85 to-[#080808]/60" />
        </div>
        {/* Ambient particles */}
        <div className="ambient-particles z-[2]">
          <span /><span /><span /><span /><span /><span />
        </div>
        
        <div className="relative z-[3] max-w-[1400px] mx-auto px-6 lg:px-12 py-32">
          <div className="max-w-2xl">
            <p className="text-[10px] tracking-[0.3em] uppercase mb-6" style={{ fontFamily: "'DM Mono', monospace", color: '#C25E3E' }}>40 EXECUTIVE-GRADE COURSES</p>
            <h1 className="text-5xl lg:text-[72px] leading-[1.0] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, color: '#F4F0E6' }}>
              Professional Development Academy
            </h1>
            <p className="text-xl leading-relaxed mb-8" style={{ fontFamily: "'Lora', serif", color: 'rgba(250,248,244,0.45)' }}>
              Higher education grade courses designed for executives and professionals. 
              Research-backed content from Harvard, Stanford, and McKinsey — completely free.
            </p>
            <div className="flex flex-wrap gap-6" style={{ color: 'rgba(250,248,244,0.4)' }}>
              <div className="flex items-center gap-2">
                <BookOpen size={20} className="text-[#C25E3E]" />
                <span className="text-sm" style={{ fontFamily: "'DM Mono', monospace" }}>{courses.length} Courses</span>
              </div>
              <div className="flex items-center gap-2">
                <GraduationCap size={20} className="text-[#C25E3E]" />
                <span className="text-sm" style={{ fontFamily: "'DM Mono', monospace" }}>Executive Level</span>
              </div>
              <div className="flex items-center gap-2">
                <Award size={20} className="text-[#C25E3E]" />
                <span className="text-sm" style={{ fontFamily: "'DM Mono', monospace" }}>Free Certificates</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[#FAF9F6] py-12 border-t border-b border-[rgba(201,168,76,0.12)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { value: '50,000+', label: 'Professionals Enrolled' },
              { value: courses.length || '100+', label: 'Free Courses' },
              { value: totalModules || '300+', label: 'Learning Modules' },
              { value: '25,000+', label: 'Certificates Issued' },
            ].map((stat) => (
              <div key={stat.label}>
                <div className="text-4xl lg:text-5xl font-bold mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#C25E3E' }}>
                  {stat.value}
                </div>
                <div className="text-sm tracking-wide" style={{ fontFamily: "'DM Mono', monospace", color: '#4A4A4A', fontWeight: 500 }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-sbt-black section-padding">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <SectionLabel>How It Works</SectionLabel>
            <h2 className="font-display text-4xl lg:text-[52px] font-bold text-sbt-white mt-4">
              Your Path to Certification
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Enroll', desc: 'Choose a course and start immediately. No payment required.', icon: Users },
              { step: '02', title: 'Learn', desc: 'Read through modules at your own pace. No deadlines.', icon: BookOpen },
              { step: '03', title: 'Quiz', desc: 'Complete quizzes after each module. Score 70% to pass.', icon: CheckCircle },
              { step: '04', title: 'Certify', desc: 'Submit your details and receive your certificate instantly.', icon: Award },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.step} className="text-center">
                  <div className="w-16 h-16 bg-sbt-gold rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon size={28} className="text-sbt-black" />
                  </div>
                  <span className="font-display text-5xl font-bold text-sbt-gold/30">{item.step}</span>
                  <h3 className="font-subheading text-xl font-bold text-sbt-white mt-2 mb-2">
                    {item.title}
                  </h3>
                  <p className="font-body text-sm text-sbt-dim">
                    {item.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Certificate Preview Section */}
      <section className="bg-gradient-to-br from-[#0a0a0c] via-sbt-black to-[#0a0a0c] section-padding overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left - Premium Certificate Preview */}
            <div className="relative">
              {/* Certificate Frame */}
              <div 
                className="relative bg-[#FFFEF9] p-1 shadow-2xl"
                style={{ 
                  boxShadow: '0 25px 80px rgba(200, 164, 72, 0.3), 0 10px 30px rgba(0,0,0,0.4)'
                }}
              >
                {/* Gold Border */}
                <div className="border-2 border-[#C8A448] p-1">
                  <div className="border border-[#C8A448]/50 p-8 lg:p-10">
                    {/* Certificate Inner Content */}
                    <div className="text-center relative">
                      {/* Decorative Corner Elements */}
                      <div className="absolute top-0 left-0 w-12 h-12 border-t-2 border-l-2 border-[#C8A448]" />
                      <div className="absolute top-0 right-0 w-12 h-12 border-t-2 border-r-2 border-[#C8A448]" />
                      <div className="absolute bottom-0 left-0 w-12 h-12 border-b-2 border-l-2 border-[#C8A448]" />
                      <div className="absolute bottom-0 right-0 w-12 h-12 border-b-2 border-r-2 border-[#C8A448]" />
                      
                      {/* Header */}
                      <div className="mb-6">
                        <p 
                          className="text-[10px] tracking-[0.4em] uppercase mb-2"
                          style={{ color: '#8B7355', fontFamily: "'DM Mono', monospace" }}
                        >
                          The She Built This Research Initiative
                        </p>
                        <h3 
                          className="text-3xl lg:text-4xl font-bold tracking-wide"
                          style={{ 
                            fontFamily: "'Cormorant Garamond', serif",
                            color: '#1a1a1a',
                            letterSpacing: '0.05em'
                          }}
                        >
                          Certificate of Completion
                        </h3>
                        <div className="w-32 h-[1px] bg-gradient-to-r from-transparent via-[#C8A448] to-transparent mx-auto mt-4" />
                      </div>

                      {/* Body */}
                      <div className="my-8">
                        <p className="text-sm text-[#5a5a5a] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                          This is to certify that
                        </p>
                        <p 
                          className="text-2xl lg:text-3xl font-bold mb-4"
                          style={{ 
                            fontFamily: "'Cormorant Garamond', serif",
                            color: '#1a1a1a',
                            borderBottom: '1px solid #C8A448',
                            display: 'inline-block',
                            paddingBottom: '4px'
                          }}
                        >
                          [Recipient Name]
                        </p>
                        <p className="text-sm text-[#5a5a5a] mt-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                          has successfully completed all requirements for
                        </p>
                        <p 
                          className="text-xl lg:text-2xl font-semibold mt-4"
                          style={{ 
                            fontFamily: "'Cormorant Garamond', serif",
                            color: '#C8A448'
                          }}
                        >
                          Leadership Fundamentals
                        </p>
                        <p className="text-xs text-[#8B7355] mt-2" style={{ fontFamily: "'DM Mono', monospace" }}>
                          Research-Based Leadership Development Programme
                        </p>
                      </div>

                      {/* Footer with Signatures */}
                      <div className="mt-8 pt-6 border-t border-[#e5e0d5]">
                        <div className="flex justify-between items-end">
                          <div className="text-left">
                            <div className="w-32 border-b border-[#1a1a1a] mb-1" />
                            <p className="text-lg italic text-[#1a1a1a]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                              Dr. Neha Dewan
                            </p>
                            <p className="text-[10px] text-[#8B7355] tracking-wide" style={{ fontFamily: "'DM Mono', monospace" }}>
                              FOUNDER & RESEARCH DIRECTOR
                            </p>
                          </div>
                          <div className="text-center">
                            {/* Seal */}
                            <div 
                              className="w-16 h-16 rounded-full border-2 border-[#C8A448] flex items-center justify-center mx-auto"
                              style={{ background: 'linear-gradient(135deg, #C8A448 0%, #8B7355 100%)' }}
                            >
                              <div className="w-12 h-12 rounded-full border border-[#FFFEF9]/50 flex items-center justify-center">
                                <span className="text-[#FFFEF9] text-[8px] font-bold tracking-wider">SBT</span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-[10px] text-[#8B7355] mb-1" style={{ fontFamily: "'DM Mono', monospace" }}>
                              CREDENTIAL ID
                            </p>
                            <p className="text-xs text-[#1a1a1a] font-mono">
                              SBT-2026-XXXX
                            </p>
                            <p className="text-[10px] text-[#8B7355] mt-2" style={{ fontFamily: "'DM Mono', monospace" }}>
                              ISSUED: MARCH 2026
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Verification URL */}
                      <div className="mt-6 pt-4">
                        <p className="text-[9px] text-[#8B7355] tracking-wider" style={{ fontFamily: "'DM Mono', monospace" }}>
                          VERIFY THIS CREDENTIAL AT SHEBUILTTHIS.CO/VERIFY
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-4 -left-4 w-8 h-8 border-t-2 border-l-2 border-sbt-gold/50" />
              <div className="absolute -bottom-4 -right-4 w-8 h-8 border-b-2 border-r-2 border-sbt-gold/50" />
            </div>

            {/* Right - Text Content */}
            <div className="text-center lg:text-left">
              <p className="text-sbt-gold font-mono text-xs tracking-[0.2em] uppercase mb-4">Your Achievement</p>
              <h2 className="font-display text-4xl lg:text-[52px] font-bold text-sbt-white leading-tight mb-6">
                Professional<br />Certification
              </h2>
              <p className="font-body text-lg text-sbt-dim leading-relaxed mb-8">
                Upon completion, receive a professional certificate from the She Built This Research Initiative. 
                Each credential is uniquely verifiable and designed to reflect the rigour of our research-backed curriculum.
              </p>
              <ul className="space-y-4 mb-10">
                {[
                  'Research-backed curriculum developed by Dr. Neha Dewan',
                  'Unique credential ID with public verification',
                  'One-click LinkedIn integration',
                  'PDF download for your professional portfolio',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sbt-dim">
                    <CheckCircle size={18} className="text-sbt-gold flex-shrink-0 mt-1" />
                    <span className="font-body text-base">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="#courses"
                  className="inline-flex items-center justify-center gap-2 h-14 px-8 bg-sbt-gold text-sbt-black font-mono text-sm tracking-wide hover:bg-sbt-white transition-colors"
                >
                  Begin Your Programme <ArrowRight size={16} />
                </a>
                <Link
                  to="/verify/sample"
                  className="inline-flex items-center justify-center gap-2 h-14 px-8 border border-sbt-gold/50 text-sbt-gold font-mono text-sm tracking-wide hover:bg-sbt-gold/10 transition-colors"
                >
                  View Sample Certificate
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Course Catalog */}
      <section className="bg-[#FAF9F6] section-padding" id="courses">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-12">
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase mb-4" style={{ fontFamily: "'DM Mono', monospace", color: '#C25E3E' }}>COURSE CATALOG</p>
              <h2 className="text-4xl lg:text-[52px] font-bold mt-1" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A1A1A' }}>
                {filteredCourses.length} Professional Courses
              </h2>
              <p className="text-lg mt-2" style={{ fontFamily: "'Lora', serif", color: '#4A4A4A' }}>
                Executive-grade, research-backed professional development
              </p>
            </div>
            
            {/* Search Box */}
            <div className="mt-6 lg:mt-0">
              <div className="relative">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#888]" />
                <input
                  type="text"
                  placeholder="Search courses..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 pr-4 py-3 w-full lg:w-80 bg-white border border-[#1A1A1A]/15 text-[#1A1A1A] placeholder:text-[#888] text-sm focus:outline-none focus:border-[#C25E3E] transition-colors"
                  style={{ fontFamily: "'DM Mono', monospace" }}
                />
              </div>
            </div>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-3 mb-10">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => {
                  setSelectedCategory(cat);
                  setVisibleCount(12);
                }}
                className={`px-5 py-2 text-sm tracking-wide transition-all ${
                  selectedCategory === cat
                    ? 'bg-[#C25E3E] text-white'
                    : 'bg-white border border-[#1A1A1A]/15 text-[#3A3A3A] hover:border-[#C25E3E] hover:text-[#C25E3E]'
                }`}
                style={{ fontFamily: "'DM Mono', monospace" }}
              >
                {cat}
                {cat !== 'All' && (
                  <span className="ml-2 opacity-50">
                    ({courses.filter(c => c.category === cat).length})
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block w-12 h-12 border-4 border-[#C25E3E] border-t-transparent rounded-full animate-spin" />
              <p className="text-sm mt-4" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(250,248,244,0.3)' }}>Loading courses...</p>
            </div>
          ) : (
            <>
              {/* Course Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {visibleCourses.map((course) => (
                  <div key={course.course_id} className="group overflow-hidden card-cinematic">
                    {/* Image */}
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={course.image}
                        alt={course.title}
                        className="w-full h-full object-cover object-center grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-3 py-1 bg-[#C25E3E] text-[#080808] text-[10px] tracking-wider uppercase" style={{ fontFamily: "'DM Mono', monospace" }}>
                          {course.category}
                        </span>
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-[#080808]/70 to-transparent" />
                    </div>

                    {/* Content */}
                    <div className="p-5">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-[10px]" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(250,248,244,0.3)' }}>{course.duration}</span>
                        <span className="text-[10px]" style={{ color: 'rgba(250,248,244,0.15)' }}>|</span>
                        <span className="text-[10px]" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(250,248,244,0.3)' }}>{course.module_count || course.modules?.length || 3} Modules</span>
                      </div>

                      <h3 className="text-lg font-bold mb-1 line-clamp-2 group-hover:text-[#C25E3E] transition-colors" style={{ fontFamily: "'Playfair Display', serif", color: '#F4F0E6' }}>
                        {course.title}
                      </h3>
                      <p className="text-[10px] mb-2" style={{ fontFamily: "'DM Mono', monospace", color: '#C25E3E' }}>{course.subtitle}</p>
                      <p className="text-xs line-clamp-2 mb-4" style={{ fontFamily: "'Lora', serif", color: 'rgba(250,248,244,0.3)' }}>
                        {course.description}
                      </p>

                      <button
                        onClick={() => handleEnroll(course.course_id)}
                        className="w-full py-2.5 border border-[#C25E3E] text-[#C25E3E] text-xs tracking-wide transition-all duration-300 hover:bg-[#C25E3E] hover:text-[#080808] flex items-center justify-center gap-2"
                        style={{ fontFamily: "'DM Mono', monospace" }}
                      >
                        {isEnrolled(course.course_id) ? 'Continue Learning' : 'Start Free'} <ArrowRight size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Load More */}
              {visibleCount < filteredCourses.length && (
                <div className="text-center mt-12">
                  <button
                    onClick={loadMore}
                    className="inline-flex items-center gap-3 px-10 py-4 border border-[#C25E3E] text-[#C25E3E] text-sm tracking-wide transition-all hover:bg-[#C25E3E] hover:text-[#080808]"
                    style={{ fontFamily: "'DM Mono', monospace" }}
                  >
                    Load More Courses ({filteredCourses.length - visibleCount} remaining)
                    <ArrowRight size={16} />
                  </button>
                </div>
              )}

              {/* No Results */}
              {filteredCourses.length === 0 && (
                <div className="text-center py-20">
                  <p className="text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif", color: 'rgba(250,248,244,0.4)' }}>No courses found</p>
                  <button
                    onClick={() => {
                      setSelectedCategory('All');
                      setSearchQuery('');
                    }}
                    className="text-sm text-[#C25E3E] hover:underline mt-4"
                    style={{ fontFamily: "'DM Mono', monospace" }}
                  >
                    Clear filters
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-sbt-black section-padding">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <SectionLabel>Success Stories</SectionLabel>
            <h2 className="font-display text-4xl font-bold text-sbt-white mt-4">
              What Our Graduates Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                quote: "The Leadership Fundamentals course gave me confidence to apply for my dream job. I got it!",
                name: 'Aisha K.',
                role: 'Product Manager, Dubai',
                image: 'https://images.pexels.com/photos/6893892/pexels-photo-6893892.jpeg?auto=compress&cs=tinysrgb&w=400',
              },
              {
                quote: "I completed all 6 courses in 3 months. The certificates helped me stand out in interviews.",
                name: 'Maria S.',
                role: 'Startup Founder, Riyadh',
                image: 'https://images.pexels.com/photos/3756679/pexels-photo-3756679.jpeg?auto=compress&cs=tinysrgb&w=400',
              },
              {
                quote: "Self-paced learning meant I could study while working full-time. Game changer.",
                name: 'Fatima H.',
                role: 'Finance Analyst, Abu Dhabi',
                image: 'https://images.pexels.com/photos/7275385/pexels-photo-7275385.jpeg?auto=compress&cs=tinysrgb&w=400',
              },
            ].map((testimonial, index) => (
              <div key={index} className="bg-sbt-black border-l-4 border-sbt-gold p-8">
                <p className="font-body text-lg text-sbt-dim italic leading-relaxed mb-6">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center gap-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-subheading font-bold text-sbt-white">{testimonial.name}</p>
                    <p className="font-mono text-xs text-sbt-dim">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 border-t border-[rgba(201,168,76,0.08)]">
        <div className="absolute inset-0 bg-[#FAF9F6]" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(201,168,76,0.5) 0%, transparent 50%)' }} />
        <div className="relative max-w-[800px] mx-auto px-6 text-center">
          <div className="w-16 h-[1px] mx-auto mb-10" style={{ background: 'linear-gradient(to right, transparent, #C25E3E, transparent)' }} />
          <h2 className="text-4xl lg:text-5xl font-bold mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#F4F0E6' }}>
            Ready to Start Learning?
          </h2>
          <p className="text-lg mb-8" style={{ fontFamily: "'Lora', serif", color: 'rgba(250,248,244,0.35)' }}>
            All courses are completely free. No credit card required. 
            Start your first course today and earn your certificate.
          </p>
          <a href="#courses" className="inline-flex items-center h-14 px-10 border border-[#C25E3E] text-[#C25E3E] text-[13px] tracking-[0.1em] uppercase transition-all hover:bg-[#C25E3E] hover:text-[#080808]" style={{ fontFamily: "'DM Mono', monospace" }}>
            Browse All Courses <ArrowRight className="ml-2 inline" size={16} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default YouthPage;
